/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 26/04/22
 */

import React from 'react';
import { RankedTester, rankWith, Scopable, StatePropsOfRenderer, UISchemaElement } from '@jsonforms/core';
import { withDisplayName } from '@/utils/withDisplayName';
import { createWithJsonFormsProps } from '../../utils/withJsonFormsProps';
import { namedMemo } from '@/utils/namedMemo';
import { JsonFormsDispatch } from '@jsonforms/react';
import { mapStateToDataReplacementProps } from '@/forms-renderers/chakra-ui/additional/DataReplacement/mapStateToDataReplacementProps';

export type DataReplacementProps = StatePropsOfRenderer;
export interface DataReplacementElement extends UISchemaElement {
    scopes?: Scopable['scope'][];
    expressions?: string[];
}

const ChakraDataReplacementRenderer = namedMemo('ChakraDataReplacementRenderer', (props: DataReplacementProps) => {
    // remove scopes and expressions to prevent loop
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { scopes, expressions, ...uischema } = props.uischema as DataReplacementElement;
    return <JsonFormsDispatch {...props} uischema={uischema} />;
});

export const chakraDataReplacementRendererTester: RankedTester = rankWith(100, (uischema) => {
    const element = uischema as DataReplacementElement;
    return (
        (element.scopes !== undefined && Array.isArray(element.scopes)) ||
        (element.expressions !== undefined && Array.isArray(element.expressions))
    );
});

const withJsonFormsDataReplacementProps = createWithJsonFormsProps(mapStateToDataReplacementProps);

export const withNamedJsonFormsDataReplacementProps = (...args: Parameters<typeof withJsonFormsDataReplacementProps>) =>
    withDisplayName(withJsonFormsDataReplacementProps(...args), 'withNamedJsonFormsDataReplacementProps');

export default withNamedJsonFormsDataReplacementProps(ChakraDataReplacementRenderer);
